import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PartyPopper, Building2, Heart, Tent, Phone, Mail, Instagram } from "lucide-react";
import { API, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

const OCCASIONS = [
  { icon: Heart, label: "Weddings" },
  { icon: Building2, label: "Corporate" },
  { icon: Tent, label: "Festivals" },
  { icon: PartyPopper, label: "Private Parties" },
];

const EMPTY_FORM = { name: "", email: "", event_type: "Catering", event_date: "", guests: "", message: "" };

export default function ContactSection() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent! We'll get back to you within 24 hours.");
      setForm(EMPTY_FORM);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="catering"
      data-testid="catering-section"
      className="relative py-24 sm:py-36 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-disco-lavender text-xs font-semibold tracking-[0.35em] uppercase mb-4">
            Catering & Events
          </p>
          <h2 className="font-display uppercase leading-[0.9] tracking-tight text-5xl sm:text-7xl text-[#F8F8F8]">
            Bring the <span className="neon-yellow">disco</span> to you
          </h2>
          <p className="mt-6 max-w-md text-sm sm:text-base text-zinc-400 font-light">
            The cart rolls to you - fully staffed, fully stocked, fully lit.
            Tell us about your event and we'll send a quote within 24 hours.
          </p>

          <div className="mt-8 space-y-3">
            <a
              href="tel:+14234630084"
              data-testid="contact-phone-link"
              className="flex items-center gap-3 text-sm text-zinc-200 hover:text-disco-yellow transition-colors duration-300 w-fit"
            >
              <Phone size={16} className="text-disco-yellow" />
              423-463-0084
            </a>
            <a
              href="mailto:Jess@discodogs.info"
              data-testid="contact-email-link"
              className="flex items-center gap-3 text-sm text-zinc-200 hover:text-disco-yellow transition-colors duration-300 w-fit"
            >
              <Mail size={16} className="text-disco-yellow" />
              Jess@discodogs.info
            </a>
            <a
              href="https://instagram.com/discohotdogs"
              target="_blank"
              rel="noreferrer"
              data-testid="contact-instagram-link"
              className="flex items-center gap-3 text-sm text-zinc-200 hover:text-disco-lavender transition-colors duration-300 w-fit"
            >
              <Instagram size={16} className="text-disco-lavender" />
              @discohotdogs
            </a>
          </div>

          <div className="mt-10 grid grid-cols-2 gap-4 max-w-md">
            {OCCASIONS.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-3 border border-white/10 bg-white/[0.03] px-4 py-3.5 transition-colors duration-300 hover:border-disco-teal/40"
              >
                <Icon size={18} className="text-disco-teal" />
                <span className="text-sm text-zinc-200">{label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.form
          data-testid="catering-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="bg-black/40 backdrop-blur-xl border border-white/10 p-7 sm:p-10 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="catering-name" className="text-zinc-300 text-xs uppercase tracking-widest">Name</Label>
              <Input
                id="catering-name"
                data-testid="catering-name-input"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Your name"
                className="bg-white/5 border-white/10 text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-disco-yellow"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="catering-email" className="text-zinc-300 text-xs uppercase tracking-widest">Email</Label>
              <Input
                id="catering-email"
                data-testid="catering-email-input"
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                placeholder="you@email.com"
                className="bg-white/5 border-white/10 text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-disco-yellow"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-zinc-300 text-xs uppercase tracking-widest">Event type</Label>
              <Select value={form.event_type} onValueChange={(v) => setForm((f) => ({ ...f, event_type: v }))}>
                <SelectTrigger data-testid="catering-event-type-select" className="bg-white/5 border-white/10 text-zinc-100">
                  <SelectValue placeholder="Pick one" />
                </SelectTrigger>
                <SelectContent className="bg-disco-surface border-white/10 text-zinc-100">
                  {["Catering", "Wedding", "Corporate Event", "Festival", "Private Party", "Other"].map((t) => (
                    <SelectItem key={t} value={t} data-testid={`catering-event-type-${t.toLowerCase().replace(/\s+/g, "-")}`}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="catering-date" className="text-zinc-300 text-xs uppercase tracking-widest">Event date</Label>
              <Input
                id="catering-date"
                data-testid="catering-date-input"
                type="date"
                value={form.event_date}
                onChange={set("event_date")}
                className="bg-white/5 border-white/10 text-zinc-100 [color-scheme:dark] focus-visible:ring-disco-yellow"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="catering-guests" className="text-zinc-300 text-xs uppercase tracking-widest">Estimated guests</Label>
            <Input
              id="catering-guests"
              data-testid="catering-guests-input"
              value={form.guests}
              onChange={set("guests")}
              placeholder="e.g. 80"
              className="bg-white/5 border-white/10 text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-disco-yellow"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="catering-message" className="text-zinc-300 text-xs uppercase tracking-widest">Tell us about it</Label>
            <Textarea
              id="catering-message"
              data-testid="catering-message-input"
              required
              rows={4}
              value={form.message}
              onChange={set("message")}
              placeholder="Where, when, and how hungry is the crowd?"
              className="bg-white/5 border-white/10 text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-disco-yellow resize-none"
            />
          </div>

          <button
            data-testid="catering-submit-button"
            type="submit"
            disabled={sending}
            className="w-full rounded-full bg-disco-yellow text-disco-void font-semibold py-3.5 text-sm transition-all duration-300 hover:shadow-[0_0_30px_rgba(226,255,0,0.45)] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? "Sending..." : "Send Enquiry"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
